import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterPanel } from '@/components/common/AdvancedFilter';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import type { UserDetail } from '@/features/users/types/user.types';
import { useGeoCascade } from '@/hooks/useGeoCascade';
import { getFilterPanelProps, type UrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import {
  getGeoFilterFields,
  getInstituteTypeFilterField,
  withRequiredFilterFields,
} from '@/utils/filterFields';
import { FILTER_KEYS, parseNumberFilter } from '@/utils/filters';
import { useMappingUserFilterField } from '../hooks/useMappingUserFilterField';

const REQUIRED_GEO_FILTER_KEYS = [FILTER_KEYS.divisionId, FILTER_KEYS.districtId] as const;

interface MappingFilterPanelProps {
  filters: UrlSyncedFilters;
  selectedUser: UserDetail | undefined;
}

export function MappingFilterPanel({ filters, selectedUser }: MappingFilterPanelProps) {
  const { filterValues } = filters;

  const { data: instituteTypes } = useQuery(activeInstituteTypesQueryOptions());
  const userField = useMappingUserFilterField(filterValues.userId ?? '', selectedUser);
  const geoOptions = useGeoCascade({
    divisionId: parseNumberFilter(filterValues[FILTER_KEYS.divisionId]),
    districtId: parseNumberFilter(filterValues[FILTER_KEYS.districtId]),
  });

  return (
    <AdvancedFilterPanel
      fields={[
        userField,
        getInstituteTypeFilterField(instituteTypes),
        ...withRequiredFilterFields(
          getGeoFilterFields(filterValues, geoOptions),
          REQUIRED_GEO_FILTER_KEYS,
        ),
        { key: 'instituteId', label: 'Institute ID', type: 'text', placeholder: 'Institute ID' },
        { key: 'eiinNumber', label: 'EIIN Number', type: 'text', placeholder: '123456' },
        { key: 'name', label: 'Institute Name', type: 'text', placeholder: 'Institute Name' },
      ]}
      {...getFilterPanelProps(filters)}
    />
  );
}
